import type {TimeTrackingImportColumnMapping} from '../../../features/time-tracking-import/types';

/**
 * Service column with the explicit entity kind. Goes to the server as entityKindColumn: for file
 * rows the value is empty (the kind is detected from the data), for rows added by hand it is
 * shift/absence. Without it an absence with a type that does not exist yet is taken for a shift.
 */
export const MANUAL_ENTITY_KIND_COLUMN = '__timiEntityKind';

/** Service column with the overtime flag: filled for the same rows as the entity kind. */
export const MANUAL_OVERTIME_COLUMN = '__timiOvertime';

export const MANUAL_ROW_MIN_HOURS = 0.25;
export const MANUAL_ROW_MAX_HOURS = 24;
export const MANUAL_ROW_HOURS_STEP = 0.25;

export type ManualImportRowKind = 'shift' | 'absence';

export interface ManualImportRow {
    id: string;
    /** Employee value exactly as it stands in the preview rows. */
    employeeValue: string;
    kind: ManualImportRowKind;
    /** ISO date: the server understands yyyy-MM-dd (see ParseDataExtensions.TryParseDate). */
    date: string;
    hours: string;
    projectOrAbsence: string;
    type: string;
    note: string;
    tags: string[];
    /** Overtime shift. An absence has no such flag — the server ignores it with a warning. */
    isOvertime: boolean;
}

export type ManualImportRowValues = Omit<ManualImportRow, 'id'>;

/**
 * Edits of the rows that came from the file: which rows are dropped from the import and which are
 * rewritten by hand. An override is keyed by the row index in the original preview (preview.rows),
 * so it does not depend on how many rows were dropped before it.
 */
export interface ImportFileRowChanges {
    excludedIndexes: number[];
    overrides: Record<number, ManualImportRowValues>;
}

export const EMPTY_FILE_ROW_CHANGES: ImportFileRowChanges = {
    excludedIndexes: [],
    overrides: {},
};

/** A preview row: either a file row (by index) or a row added by hand (by id). */
export type ImportRowTarget =
    | {type: 'file'; index: number}
    | {type: 'manual'; id: string};

/**
 * Order of the rows in the generated file: first the file rows that are left, then the manual ones.
 * Response rows are matched against whatever produced them by that order.
 */
export const buildImportRowTargets = (
    fileRowCount: number,
    fileRowChanges: ImportFileRowChanges,
    manualRows: ManualImportRow[]
): ImportRowTarget[] => [
    ...Array.from({length: fileRowCount}, (_, index) => index)
        .filter((index) => !fileRowChanges.excludedIndexes.includes(index))
        .map((index): ImportRowTarget => ({type: 'file', index})),
    ...manualRows.map((manualRow): ImportRowTarget => ({type: 'manual', id: manualRow.id})),
];

/** What the user picked in the add-entry form, in interface terms. */
export interface ManualImportRowSelection {
    employeeValue: string;
    kind: ManualImportRowKind;
    date: string;
    hours: string;
    /** Project: shifts only. */
    projectName: string;
    /** Shift type or absence type — in the form it is a single dropdown, like in the timesheet. */
    typeName: string;
    note: string;
    tags: string[];
    isOvertime: boolean;
}

export interface ImportEntityOption {
    name: string;
    /** The entity does not exist yet — it is created by this very import. */
    isPending: boolean;
    /** Default type of the dictionary: it fills the rows that have no explicit type. */
    isDefault: boolean;
}

export interface ImportEntityDictionaryItem {
    name: string;
    default?: boolean;
}

export interface ManualImportRowOptions {
    projects: ImportEntityOption[];
    absenceTypes: ImportEntityOption[];
    shiftTypes: ImportEntityOption[];
    tags: ImportEntityOption[];
}

let manualRowSequence = 0;

export const createManualImportRowId = () => {
    manualRowSequence += 1;

    return `manual-row-${manualRowSequence}`;
};

/**
 * Spreads the form selection over the file columns. An absence has neither shift type nor tags,
 * and the server reads the absence type from the "Project/Absence" column when type is empty.
 */
export const buildManualImportRowValues = (
    selection: ManualImportRowSelection
): ManualImportRowValues => {
    const isAbsence = selection.kind === 'absence';

    return {
        employeeValue: selection.employeeValue,
        kind: selection.kind,
        date: selection.date,
        hours: selection.hours,
        projectOrAbsence: isAbsence ? selection.typeName : selection.projectName,
        type: isAbsence ? '' : selection.typeName,
        note: selection.note,
        tags: isAbsence ? [] : selection.tags,
        isOvertime: !isAbsence && selection.isOvertime,
    };
};

export const createManualImportRow = (values: ManualImportRowValues): ManualImportRow => ({
    ...values,
    id: createManualImportRowId(),
});

export const parseManualRowHours = (value: string) => {
    const normalizedValue = value.trim().replace(',', '.');

    if (!normalizedValue) {
        return Number.NaN;
    }

    return Number.parseFloat(normalizedValue);
};

export const getManualImportRowValue = (
    row: ManualImportRow,
    header: string,
    columnMapping: TimeTrackingImportColumnMapping
) => {
    switch (header) {
        case MANUAL_ENTITY_KIND_COLUMN:
            return row.kind;
        case columnMapping.employeeColumn:
            return row.employeeValue;
        case columnMapping.dateColumn:
            return row.date;
        case columnMapping.hoursColumn:
            return row.hours;
        case columnMapping.projectOrAbsenceColumn:
            return row.projectOrAbsence;
        case columnMapping.typeColumn:
            return row.kind === 'absence' ? '' : row.type;
        case columnMapping.noteColumn:
            return row.note;
        case MANUAL_OVERTIME_COLUMN:
            return row.kind === 'absence' ? '' : String(row.isOvertime);
        case columnMapping.tagsColumn:
            return row.kind === 'absence' ? '' : row.tags.join(', ');
        default:
            return '';
    }
};

/**
 * The service columns are only needed when the file has rows filled by hand: added and rewritten
 * ones. Otherwise the file is sent in exactly the previous format.
 */
const hasManuallyFilledRows = (
    manualRows: ManualImportRow[],
    fileRowChanges: ImportFileRowChanges
) => manualRows.length > 0 || Object.keys(fileRowChanges.overrides).length > 0;

export const getImportEntityKindColumn = (
    manualRows: ManualImportRow[],
    fileRowChanges: ImportFileRowChanges = EMPTY_FILE_ROW_CHANGES
) => hasManuallyFilledRows(manualRows, fileRowChanges) ? MANUAL_ENTITY_KIND_COLUMN : '';

/** The overtime flag belongs to the same rows as the explicit entity kind. */
export const getImportOvertimeColumn = (
    manualRows: ManualImportRow[],
    fileRowChanges: ImportFileRowChanges = EMPTY_FILE_ROW_CHANGES
) => hasManuallyFilledRows(manualRows, fileRowChanges) ? MANUAL_OVERTIME_COLUMN : '';

const entityOptionCollator = new Intl.Collator(undefined, {sensitivity: 'base', numeric: true});

const normalizeEntityOptionName = (value: string) => value.trim().toLowerCase();

export const buildImportEntityOptions = (
    existingItems: ImportEntityDictionaryItem[],
    pendingNames: string[]
): ImportEntityOption[] => {
    const usedNames = new Set<string>();
    const options: ImportEntityOption[] = [];

    const addOptions = (items: ImportEntityDictionaryItem[], isPending: boolean) => {
        items
            .map((item) => ({...item, name: item.name.trim()}))
            .filter((item) => {
                if (!item.name) {
                    return false;
                }

                const normalizedName = normalizeEntityOptionName(item.name);

                if (usedNames.has(normalizedName)) {
                    return false;
                }

                usedNames.add(normalizedName);
                return true;
            })
            .sort((left, right) => entityOptionCollator.compare(left.name, right.name))
            .forEach((item) => options.push({
                name: item.name,
                isPending,
                // An entity created by this import can never be the default one.
                isDefault: !isPending && Boolean(item.default),
            }));
    };

    addOptions(existingItems, false);
    addOptions(pendingNames.map((name) => ({name})), true);

    return options;
};
